/* eslint no-shadow: 0 */
/* eslint-disable max-lines */
import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import ControlledFeatureService from 'services/ControlledFeatureService';
import createMessagingInstance from 'services/MessagingService/MessagingService';
import {
  PRIVATE_DIRECTORY_USER_BALANCE,
  PRIVATE_DIRECTORY_ROOM_OFFLINE,
  PRIVATE_DIRECTORY_USER_NOTIFICATION,
  PRIVATE_DIRECTORY_USER_NOTIFICATION_CHANGED,
  UPDATE_BALANCE,
  USER_JOINED_FANCLUB_NOTIFICATION,
} from 'services/MessagingService/EventNames';
import ModalService from 'services/ModalService';
import { EWALLET_ACCOUNT_URL as ewalletAccountUrl } from 'features/Payout/Api';
import { getUserState } from 'pages/User/State';
import { AVAILABLE_COUNTRIES } from 'enum/availableCountries';
import { AVAILABLE_LANGUAGES } from 'enum/availableLanguages';
import allUserGroups from 'enum/userGroups';
import { BROADCAST_STATUS } from 'enum/videoPlayer';
import { usernameOrImpersonate } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';

import { USER_NOTIFICATION_STATE_LABELS } from '../firebase/firebaseStructure';
import firebaseNode from '../firebase/firebaseNode';
import { FirebaseMain } from '../firebase/firebase';

import {
  DISCONNECT_FROM_TWITTER_URL,
  GUEST_INFO_URL,
  TIPPING_POLLING_URL,
  TWITTER_INFO_URL,
} from './Api';


const messagingService = createMessagingInstance();
const SET_ACTIVE_PERFORMER = 'cam4/SET_ACTIVE_PERFORMER';
const SET_SHOW_UN_DOCKED_VIDEO = 'cam4/SET_SHOW_UN_DOCKED_VIDEO';
const SET_VOLUME_FOR_VIDEO = 'cam4/SET_VOLUME_FOR_VIDEO';
const SET_BROADCAST_STATUS = 'cam4/SET_BROADCAST_STATUS';
const SET_SHOW_REGISTRATION_MODAL = 'cam4/SET_SHOW_REGISTRATION_MODAL';
const GET_BALANCE = 'cam4/GET_BALANCE';
const IS_BALANCE_RETURNED = 'cam4/IS_BALANCE_RETURNED';
const SET_BALANCE_ERROR = 'cam4/SET_BALANCE_ERROR';
const SAVE_INTERVAL = 'cam4/SAVE_INTERVAL';
const SET_TWITTER_INFO = 'cam4/SET_TWITTER_INFO';
const TOGGLE_EMAIL_EXIST_MODAL = 'cam4/TOGGLE_EMAIL_EXIST_MODAL';

const initialState = Immutable.fromJS({
  activeBroadcast: {},
  balance: 0,
  isBalanceReturned: false,
  balanceError: false,
  broadcastStatus: BROADCAST_STATUS.PLAYING,
  visibleVideo: true,
  volumeForVideo: true,
  register: {
    showRegistrationModal: false,
    registrationModalCaller: null,
    closedFirstTime: false,
  },
  privateShowBalanceInterval: 0,
  twitterInfo: {},
  isEmailExistsModalOpen: false,
});

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_ACTIVE_PERFORMER:
      return state.set('activeBroadcast', action.activeBroadcast);

    case SET_SHOW_UN_DOCKED_VIDEO:
      return state.set('visibleVideo', action.visibleVideo);

    case SET_VOLUME_FOR_VIDEO:
      return state.set('volumeForVideo', action.volumeForVideo);

    case SET_BROADCAST_STATUS:
      return state.set('broadcastStatus', action.broadcastStatus);

    case SET_SHOW_REGISTRATION_MODAL: {
      if (!action.showRegistrationModal) {
        // eslint-disable-next-line no-param-reassign
        state = state.setIn(['register', 'closedFirstTime'], true);
      }

      return state
        .setIn(['register', 'showRegistrationModal'], action.showRegistrationModal)
        .setIn(['register', 'registrationModalCaller'], action.registrationModalCaller);
    }

    case GET_BALANCE:
      return state.set('balance', action.balance);

    case IS_BALANCE_RETURNED:
      return state.set('isBalanceReturned', action.isBalanceReturned);

    case SET_BALANCE_ERROR:
      return state.set('balanceError', action.balanceError);

    case SAVE_INTERVAL:
      return state.set('privateShowBalanceInterval', action.interval);

    case SET_TWITTER_INFO:
      return state.set('twitterInfo', action.twitterInfo);

    case TOGGLE_EMAIL_EXIST_MODAL:
      return state.set('isEmailExistsModalOpen', action.payload);

    default:
      return state;
  }
}

export const NAME = 'app';

function parseActiveBroadcast(activeBroadcast = {}) {
  return {
    type: SET_ACTIVE_PERFORMER,
    activeBroadcast,
  };
}

function parseShowUnDockedVideo(visibleVideo) {
  return {
    type: SET_SHOW_UN_DOCKED_VIDEO,
    visibleVideo,
  };
}

function parseVolumeForVideo(volumeForVideo) {
  return {
    type: SET_VOLUME_FOR_VIDEO,
    volumeForVideo,
  };
}

function parseBroadcastStatus(broadcastStatus) {
  return {
    type: SET_BROADCAST_STATUS,
    broadcastStatus,
  };
}

function parseShowRegistrationModal(showRegistrationModal, registrationModalCaller) {
  return {
    type: SET_SHOW_REGISTRATION_MODAL,
    showRegistrationModal,
    registrationModalCaller,
  };
}

function setBalance(balance) {
  return {
    type: GET_BALANCE,
    balance,
  };
}

function setIsBalanceReturned(isBalanceReturned) {
  return {
    type: IS_BALANCE_RETURNED,
    isBalanceReturned,
  };
}

function setBalanceError(balanceError) {
  return {
    type: SET_BALANCE_ERROR,
    balanceError,
  };
}

function saveInterval(interval) {
  return {
    type: SAVE_INTERVAL,
    interval,
  };
}

function setActiveBroadcast(activeBroadcast = {}) {
  return (dispatch) => dispatch(parseActiveBroadcast(activeBroadcast));
}

function setShowUnDockedVideo(visibleVideo) {
  return (dispatch) => dispatch(parseShowUnDockedVideo(visibleVideo));
}

function setVolumeForVideo(volumeForVideo) {
  return (dispatch) => dispatch(parseVolumeForVideo(volumeForVideo));
}

function setBroadcastStatus(broadcastStatus = {}) {
  return (dispatch) => dispatch(parseBroadcastStatus(broadcastStatus));
}

function setShowRegistrationModal(showRegistrationModal, registrationModalCaller) {
  return (dispatch) => dispatch(parseShowRegistrationModal(showRegistrationModal, registrationModalCaller));
}

function setTwitterInfo(twitterInfo) {
  return {
    type: SET_TWITTER_INFO,
    twitterInfo,
  };
}

function getTwitterInfo(username) {
  const URL = ApiService.substitute(TWITTER_INFO_URL, { username: encodeURIComponent(username) });

  return (dispatch) =>
    ApiService.get(URL)
      .then((res) => {
        dispatch(setTwitterInfo(res.data));
      })
      .catch((error) => parseCatch(error));
}

function disconnectFromTwitter(callback) {
  return (dispatch) =>
    ApiService.get(DISCONNECT_FROM_TWITTER_URL)
      .then((res) => {
        dispatch(setTwitterInfo(res.data));
        callback?.();
      })
      .catch((error) => parseCatch(error));
}

function getBalanceInfo(username, user, passedDispatch) {
  const { accessHash } = user;
  const operation = 'getBalance';
  const params = {
    operation,
    'user_id': username,
    key: accessHash,
  };

  const useEwallet = ControlledFeatureService.setUser(user).has(allUserGroups.USE_EWALLET);
  if (useEwallet) {
    const BALANCE_TYPE_WALLET = 'WALLET';
    const url = `${encodeURI(
      ewalletAccountUrl(usernameOrImpersonate(user.username))
    )}?accountType=${BALANCE_TYPE_WALLET}`;

    return (dispatch) => {
      dispatch(setIsBalanceReturned(false));
      dispatch(setBalanceError(false));
      ApiService.get(url)
        .then((res) => {
          if (res.status === StatusCodes.OK && Array.isArray(res.data) && res.data.length) {
            const responseForTypeWallet = res.data.filter((item) => item.type === BALANCE_TYPE_WALLET);
            const userBalanceObject = responseForTypeWallet[0];

            messagingService.emit(UPDATE_BALANCE, parseInt(userBalanceObject.balance, 10));

            if (userBalanceObject && dispatch) {
              dispatch(setBalance(userBalanceObject.balance * 1));
            } else if (userBalanceObject && passedDispatch) {
              passedDispatch(setBalance(userBalanceObject.balance * 1));
            }
          }

          dispatch(setIsBalanceReturned(true));

          return res;
        })
        .catch((error) => {
          dispatch(setIsBalanceReturned(true));
          dispatch(setBalanceError(true));
          parseCatch(error);
        });
    };
  }

  const url = `${TIPPING_POLLING_URL}?params=${JSON.stringify(params)}`;

  return (dispatch) => {
    dispatch(setIsBalanceReturned(false));
    dispatch(setBalanceError(false));
    ApiService.get(url, { withCredentials: false })
      .then((res) => {
        if (res.status === StatusCodes.OK && res.data.userbalance) {
          messagingService.emit(UPDATE_BALANCE, parseInt(res.data.userbalance, 10));
          if (dispatch) {
            dispatch(setBalance(parseInt(res.data.userbalance, 10) !== -1 ? res.data.userbalance * 1 : 0));
            dispatch(setIsBalanceReturned(true));
            messagingService.emit(PRIVATE_DIRECTORY_USER_BALANCE, parseInt(res.data.userbalance, 10));
          } else if (passedDispatch) {
            passedDispatch(setBalance(parseInt(res.data.userbalance, 10) !== -1 ? res.data.userbalance * 1 : 0));
            passedDispatch(setIsBalanceReturned(true));
            messagingService.emit(PRIVATE_DIRECTORY_USER_BALANCE, parseInt(res.data.userbalance, 10));
          }
        }

        return res;
      })
      .catch((error) => {
        dispatch(setIsBalanceReturned(true));
        dispatch(setBalanceError(true));
        messagingService.emit(PRIVATE_DIRECTORY_USER_BALANCE, 0);
        parseCatch(error);
      });
  };
}

function updateBalance(balance) {
  return (dispatch) => dispatch(setBalance(balance));
}

function setPrivateShowCounter(user) {
  const minute = 60000;

  return (dispatch) => {
    const interval = setInterval(getBalanceInfo(user.username, user, dispatch), minute);
    dispatch(saveInterval(interval));
  };
}

function resetPrivateShowCounter(interval) {
  clearInterval(interval);

  return (dispatch) => dispatch(saveInterval(0));
}

function toggleEmailExistsModal(flag) {
  return {
    type: TOGGLE_EMAIL_EXIST_MODAL,
    payload: Boolean(flag),
  };
}

function startGuestBillingSession() {
  const delay = 1000;
  const url = `${GUEST_INFO_URL}?_=${new Date().getTime()}`;

  return (dispatch) => {
    ApiService.get(url)
      .then((res) => {
        const showUpgradeToRegular = true;
        if (res.data) {
          const status = res.data.trim();
          const allResponses = {
            SUCCESS: 'SUCCESS',
            EMAIL_EXISTS: 'EMAIL_EXISTS',
            PENDING: 'PENDING',
            ERROR: 'ERROR',
          };
          switch (status) {
            case allResponses.SUCCESS:
              dispatch(getUserState(showUpgradeToRegular));
              break;
            case allResponses.EMAIL_EXISTS:
              // show conflict modal
              dispatch(toggleEmailExistsModal(true));
              break;
            case allResponses.PENDING:
              setTimeout(() => {
                dispatch(startGuestBillingSession());
              }, delay);
              break;

            case allResponses.ERROR:
              logger.error('There was an error starting guest billing session');
              break;
            default:
              break;
          }
        }
      })
      .catch((err) => parseCatch(err));
  };
}

function appendExoClickTag() {
  const exadsErrorScript = document.body.querySelector('[data-goal=12d739fa72c8a47a685822b8802622b4]');
  if (exadsErrorScript) {
    document.body.removeChild(exadsErrorScript);
  }

  const exadsSuccessScript = document.body.querySelector('[data-goal=ea10ca9d671e112c6777a213168403c6]');
  if (exadsSuccessScript) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'application/javascript';
  script.src = 'https://a.exoclick.com/tag_gen.js';
  script.setAttribute('data-goal', 'ea10ca9d671e112c6777a213168403c6');
  document.body.append(script);
}

function watchUserNotificationsUpdate(user, { updateUserStatusToGold, guestUser }) {
  const userNotificationStateRef = FirebaseMain.child(firebaseNode.USER_NOTIFICATION).child(
    user.username.toLowerCase()
  );

  return (dispatch) => {
    userNotificationStateRef.on('value', (snapshot) => {
      const timeout = 5000;
      const message = snapshot.val();
      const messageType = message?.a;
      if (!messageType) {
        return;
      }

      switch (messageType) {
        case USER_NOTIFICATION_STATE_LABELS.PRIVATE_DIRECTORY_USER_NOTIFICATION: {
          messagingService.emit(PRIVATE_DIRECTORY_USER_NOTIFICATION, message);
          break;
        }
        case USER_NOTIFICATION_STATE_LABELS.PRIVATE_DIRECTORY_USER_NOTIFICATION_CHANGED: {
          messagingService.emit(PRIVATE_DIRECTORY_USER_NOTIFICATION_CHANGED);
          break;
        }
        case USER_NOTIFICATION_STATE_LABELS.PRIVATE_DIRECTORY_ROOM_OFFLINE: {
          messagingService.emit(PRIVATE_DIRECTORY_ROOM_OFFLINE);
          break;
        }
        case USER_NOTIFICATION_STATE_LABELS.USER_JOINED_FANCLUB: {
          messagingService.emit(USER_JOINED_FANCLUB_NOTIFICATION);
          break;
        }
        case 'ji': {
          ModalService.showModal(ModalService.MODAL_KEYS.SHOW_INVITE_MODAL, {
            sender: message.i,
            user,
          });
          break;
        }
        case 'gu': {
          updateUserStatusToGold();
          dispatch(getBalanceInfo(user.username, user));
          setTimeout(() => dispatch(getUserState()), timeout);
          break;
        }
        case 'b': {
          if (guestUser) {
            dispatch(startGuestBillingSession());
          } else {
            dispatch(getBalanceInfo(user.username, user));
            setTimeout(() => dispatch(getUserState()), timeout);
          }
          appendExoClickTag();
          break;
        }
        case 'twc': {
          const twcStatus = {
            fld: false,
            scs: true,
          };
          if (twcStatus[message.st]) {
            dispatch(getTwitterInfo(user.username));
          } else {
            dispatch(
              setTwitterInfo({ connected: false })
            );
          }
          break;
        }
        default: {
          break;
        }
      }
    });
  };
}

const balance = (state) => state[NAME].getIn(['balance']);
const isBalanceReturned = (state) => state[NAME].getIn(['isBalanceReturned']);
const balanceError = (state) => state[NAME].getIn(['balanceError']);
const activeBroadcast = (state) => state[NAME].get('activeBroadcast');
const visibleVideo = (state) => state[NAME].get('visibleVideo');
const volumeForVideo = (state) => state[NAME].get('volumeForVideo');
const broadcastStatus = (state) => state[NAME].get('broadcastStatus');
const showRegistrationModal = (state) => state[NAME].getIn(['register', 'showRegistrationModal']);
const registrationModalCaller = (state) => state[NAME].getIn(['register', 'registrationModalCaller']);
const registrationModalClosedFirstTime = (state) => state[NAME].getIn(['register', 'closedFirstTime']);
const privateShowBalanceInterval = (state) => state[NAME].get('privateShowBalanceInterval');
const twitterInfo = (state) => state[NAME].get('twitterInfo');
const isEmailExistsModalOpen = (state) => state[NAME].get('isEmailExistsModalOpen');

export const selector = createStructuredSelector({
  balance,
  activeBroadcast,
  visibleVideo,
  volumeForVideo,
  broadcastStatus,
  showRegistrationModal,
  registrationModalCaller,
  registrationModalClosedFirstTime,
  availableCountries: () => AVAILABLE_COUNTRIES,
  availableLanguages: () => AVAILABLE_LANGUAGES,
  privateShowBalanceInterval,
  isBalanceReturned,
  balanceError,
  isEmailExistsModalOpen,
  twitterInfo,
});

export const actionCreators = {
  setActiveBroadcast,
  setShowUnDockedVideo,
  setVolumeForVideo,
  setBroadcastStatus,
  setShowRegistrationModal,
  getBalanceInfo,
  updateBalance,
  setBalance,
  setIsBalanceReturned,
  setPrivateShowCounter,
  resetPrivateShowCounter,
  watchUserNotificationsUpdate,
  getTwitterInfo,
  disconnectFromTwitter,
  setTwitterInfo,
  toggleEmailExistsModal,
};
